import { FetchResult } from 'src/types/TApiQueries';
import {
  AssetSchema,
  SchemaPresetListResponse,
  SchemaPresetsCreateResponse,
  SchemaCreateRequest,
  SchemaUpdateRequest,
  SchemaResponse,
} from 'src/types/TSchemes';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';

export type SchemaStatusFilter = 'ACTIVE' | 'INACTIVE' | 'ALL';

export async function getSchemas(
  accessToken: string,
  status: SchemaStatusFilter = 'ACTIVE',
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetschemas?status=${status}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getSchema(accessToken: string, schemaId: number): Promise<FetchResult<AssetSchema>> {
  const url = `${config.hostUrl}/api/v1/assetschemas/${schemaId}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Get available schema presets catalog
 * GET /api/v1/assetschemas/presets
 */
export async function getSchemaPresets(
  accessToken: string,
): Promise<FetchResult<SchemaPresetListResponse>> {
  const url = `${config.hostUrl}/api/v1/assetschemas/presets`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/**
 * Create schemas from selected presets
 * POST /api/v1/assetschemas/presets
 */
export async function createSchemasFromPresets(
  accessToken: string,
  presetIds: string[],
): Promise<FetchResult<SchemaPresetsCreateResponse>> {
  const url = `${config.hostUrl}/api/v1/assetschemas/presets`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ preset_ids: presetIds }),
  });
}

/**
 * Create a new custom asset schema
 * POST /api/v1/schema
 */
export async function createSchema(
  accessToken: string,
  data: SchemaCreateRequest,
): Promise<FetchResult<SchemaResponse>> {
  const url = `${config.hostUrl}/api/v1/schema`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing asset schema
 * POST /api/v1/schema  (with id in body)
 */
export async function updateSchema(
  accessToken: string,
  schemaId: number,
  data: SchemaUpdateRequest,
): Promise<FetchResult<SchemaResponse>> {
  const url = `${config.hostUrl}/api/v1/schema`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ id: schemaId, ...data }),
  });
}

/**
 * Activate / deactivate a schema.
 * PUT /api/v1/assetschemas/{schemaId}/status
 *
 * Use this — NOT `updateSchema` — to flip a schema's status. Two reasons:
 *
 *  1. `updateSchema` posts to `/api/v1/schema`, which sits behind the
 *     CUSTOM_SCHEMAS (Enterprise) plan gate. Toggling status through it 403s
 *     for exactly the accounts that need to deactivate: `delete` refuses an
 *     ACTIVE schema, so a gated customer could not deactivate → could not
 *     delete → could not free an `asset_schemas` cap slot, and the cap error's
 *     own advice ("Delete one to swap presets") was unreachable.
 *  2. It sends the entire schema definition to change one field. This endpoint
 *     is status-only and cannot touch the schema JSON.
 *
 * Activation is cap-checked server-side and returns 400 `schema_limit_reached`.
 */
export async function setSchemaStatus(
  accessToken: string,
  schemaId: number,
  status: 'ACTIVE' | 'INACTIVE',
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetschemas/${schemaId}/status`;

  return performFetchWithLogging(url, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ status }),
  });
}

/**
 * Delete an asset schema
 * DELETE /api/v1/assetschemas/{schemaId}
 */
export async function deleteSchema(
  accessToken: string,
  schemaId: number,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assetschemas/${schemaId}`;

  return performFetchWithLogging(url, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}
